import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { LogService } from '../../services/log.service';
import { ProductService } from '../../services/product.service';

// Importaciones de interfaces.
import { IProduct } from '../../interfaces/product.interface';

// Importaciones de modelos.
import { Log } from '../../models/log.model';

/**
 * Componente de diálogo para la eliminación del producto.
 *
 * Este componente proporciona una interfaz para confirmar y realizar la eliminación del producto.
 * Se muestra como un cuadro de diálogo, permitiendo al usuario confirmar la acción antes de proceder con la eliminación.
 */
@Component({
  selector: 'app-product-delete-dialog',
  templateUrl: './product-delete-dialog.component.html',
  standalone: false,
})
export class ProductDeleteDialogComponent {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de eliminación del producto.
   */
  readonly dialogRef: MatDialogRef<ProductDeleteDialogComponent> = inject(
    MatDialogRef<ProductDeleteDialogComponent>
  );

  /**
   * Datos del producto proporcionados al diálogo.
   *
   * Se inyectan desde MAT_DIALOG_DATA y son de solo lectura.
   */
  private readonly product: IProduct = inject<IProduct>(MAT_DIALOG_DATA);

  /**
   * Indica si la operación de eliminado está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se elimina el producto.
   */
  loading: boolean = false;

  /**
   * Lista de errores.
   *
   * Contiene los mensajes de error generados si la operación de eliminado falla.
   */
  errors: string[] = [];

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param logService - Servicio para gestionar los registros.
   * @param productService - Servicio para gestionar la información de los productos.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private logService: LogService,
    private productService: ProductService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Confirma y elimina el host actual.
   *
   * Muestra un diálogo de confirmación antes de proceder con la eliminación.
   * Si la eliminación es exitosa, cierra el diálogo.
   */
  async delete(): Promise<void> {
    this.loading = true;
    this.errors = [];

    try {
      const productResponse: string = await this.productService.delete(
        this.product
      );

      // Añadir un registro de eliminación del producto.
      try {
        await this.logService.create(
          new Log({
            description: `Ha eliminado el producto '${this.product.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de eliminación del producto.'
          );
        }
      }

      this.snackBar.open(productResponse, '', { duration: 5000 });

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loading = false;
  }
}
